import { ColumnMetadata, ColumnTypes } from "~/components/DataGrid";
import { DataGridDollarPercentChangeComboCell } from "~/components/DataGrid/DataGridDollarPercentChangeComboCell";
import { DataGridNumberPercentChangeComboCell } from "~/components/DataGrid/DataGridNumberPercentChangeComboCell";
import { Link } from "~/components/Link";
import { Emdash } from "~/components/Punctuation";
import { getManagerURL, getNetworkURL, getPlatformURL } from "~/routes";
import { ManagerColumns, ManagerWithAggregates } from "../..";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";
import { Icon } from "~/components/Icon";
import { getBadgeClassName } from "~/components/Badge/component";
import { ImageLoader } from "~/components/ImageLoader";
import { DISTRIBUTION_TYPE_TOOLTIPS, RWA_COIN_ICON } from "~/consts";
import { Tooltip } from "~/components/Tooltip";
import { AssetManagersUserEvents } from "~/modules/analytics/events/asset_managers";

// // // //

export const RANK_COLUMN: ColumnMetadata<
    ManagerWithAggregates,
    ManagerColumns
> = {
    slug: "rank",
    label: "#",
    type: ColumnTypes.number,
    width: 60,
    maxWidth: 60,
    cellValue: (manager) => {
        return (manager as any).rank;
    },
};

export const NAME_COLUMN: ColumnMetadata<
    ManagerWithAggregates,
    ManagerColumns
> = {
    slug: ManagerColumns.name,
    label: "Name",
    type: ColumnTypes.name,
    width: 250,
    sortsOn: ManagerColumns.name,
    render: (manager) => {
        return (
            <Link
                href={getManagerURL({ managerSlug: manager.slug })}
                className="flex items-center gap-2"
                variant="default"
                analyticsLabel={AssetManagersUserEvents.TableLinkClick}
                analyticsEventProperties={{
                    manager: manager.slug,
                }}
            >
                <ImageLoader
                    src={manager.icon_url || RWA_COIN_ICON}
                    alt={`${manager.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-4 w-4 min-w-4 rounded-full"
                />
                <span className="dg-overflow">{manager.name}</span>
            </Link>
        );
    },
};

export const RWA_ASSET_COUNT_COLUMN: ColumnMetadata<
    ManagerWithAggregates,
    ManagerColumns
> = {
    slug: ManagerColumns.assetCount,
    label: "RWA Count",
    type: ColumnTypes.number,
    width: 125,
    maxWidth: 130,
    sortsOn: ManagerColumns.assetCount,
    cellValue: (manager) => {
        return manager.rwa_asset_count;
    },
};

export const DISTRIBUTED_RWA_TOTAL_VALUE_COLUMN: ColumnMetadata<
    ManagerWithAggregates,
    ManagerColumns
> = {
    slug: ManagerColumns.distributedValue,
    label: (
        <>
            <p>RWA Value</p>
            <p className="text-xs">(Distributed)</p>
        </>
    ),
    tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
    type: ColumnTypes.dollar,
    sortsOn: ManagerColumns.distributedValue,
    width: 175,
    render: (item) => {
        const measure = item.distributed_value;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const REPRESENTED_RWA_TOTAL_VALUE_COLUMN: ColumnMetadata<
    ManagerWithAggregates,
    ManagerColumns
> = {
    slug: ManagerColumns.representedValue,
    label: (
        <>
            <p>RWA Value</p>
            <p className="text-xs">(Represented)</p>
        </>
    ),
    tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
    type: ColumnTypes.dollar,
    sortsOn: ManagerColumns.representedValue,
    width: 175,
    render: (item) => {
        const measure = item.represented_value;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const RWA_HOLDERS_COUNT_COLUMN: ColumnMetadata<
    ManagerWithAggregates,
    ManagerColumns
> = {
    slug: ManagerColumns.holdingAddressesCount,
    label: "RWA Holders",
    tooltip: "Excludes Stablecoins",
    type: ColumnTypes.number,
    sortsOn: ManagerColumns.holdingAddressesCount,
    width: 150,
    render: (item) => {
        const measure = item.rwa_holders_count;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridNumberPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

// export const TRANSFER_VOLUME_COLUMN: ColumnMetadata<
//     ManagerWithAggregates,
//     ManagerColumns
// > = {
//     slug: ManagerColumns.trailing30DayTransferVolume,
//     label: "Transfer Volume",
//     type: ColumnTypes.dollar,
//     sortsOn: ManagerColumns.trailing30DayTransferVolume,
//     width: 175,
//     visible: true,
//     render: (item) => {
//         const measure = getMeasureValue(
//             item,
//             "trailing_30_day_transfer_volume",
//         );

//         if (measure === null) {
//             return <Emdash />;
//         }

//         return (
//             <DataGridDollarPercentChangeComboCell
//                 value={measure.val}
//                 pct_change_interval="30d"
//                 pct_change_value={measure.chg_30d_pct}
//             />
//         );
//     },
// };

// export const ACTIVE_ADDRESSES_COLUMN: ColumnMetadata<
//     ManagerWithAggregates,
//     ManagerColumns
// > = {
//     slug: ManagerColumns.trailing30DayActiveAddressesCount,
//     label: "Active Addresses",
//     type: ColumnTypes.number,
//     sortsOn: ManagerColumns.trailing30DayActiveAddressesCount,
//     width: 150,
//     visible: true,
//     render: (item) => {
//         const measure = getMeasureValue(
//             item,
//             "trailing_30_day_active_addresses_count",
//         );

//         if (measure === null) {
//             return <Emdash />;
//         }

//         return (
//             <DataGridNumberPercentChangeComboCell
//                 value={measure.val}
//                 pct_change_interval="30d"
//                 pct_change_value={measure.chg_30d_pct}
//             />
//         );
//     },
// };

export const ASSET_CLASSES_COLUMN: ColumnMetadata<
    ManagerWithAggregates,
    ManagerColumns
> = {
    slug: "asset_classes",
    label: "Offered Asset Classes",
    type: ColumnTypes.string,
    width: 250,
    render: (manager) => {
        const stats = manager.asset_class_stats;

        if (!stats || stats.length === 0) {
            return <Emdash />;
        }

        const metadata = stats
            .sort((a, b) => {
                return (
                    (b.bridged_token_value_dollar?.val ?? 0) -
                    (a.bridged_token_value_dollar?.val ?? 0)
                );
            })
            .map((stat) => {
                return getAssetClassLinkMetadata(stat.id);
            });

        return (
            <div className="flex items-center gap-2">
                {metadata.slice(0, 2).map((meta) => {
                    if (meta.href === null) {
                        return (
                            <div
                                key={meta.label}
                                className={getBadgeClassName({
                                    size: "sm",
                                    variant: "gray",
                                    hover: false,
                                    rounded: "sm",
                                    className: "flex items-center gap-2",
                                })}
                            >
                                {meta.icon && (
                                    <Icon className="h-3" icon={meta.icon} />
                                )}
                                {meta.label}
                            </div>
                        );
                    }
                    return (
                        <Link
                            key={meta.label}
                            href={meta.href}
                            analyticsLabel={
                                AssetManagersUserEvents.ClickAssetClassLink
                            }
                            analyticsEventProperties={{
                                asset_class: meta.label,
                            }}
                            className={getBadgeClassName({
                                size: "sm",
                                variant: "gray",
                                hover: true,
                                rounded: "sm",
                                className: "flex items-center gap-2",
                            })}
                        >
                            {meta.icon && (
                                <Icon className="h-3" icon={meta.icon} />
                            )}
                            {meta.label}
                        </Link>
                    );
                })}
                {metadata.length - 2 > 0 && (
                    <span className="text-xs text-sidebar-foreground">
                        +{metadata.length - 2} more
                    </span>
                )}
            </div>
        );
    },
};

export const DISTRIBUTION_PLATFORM_COLUMN: ColumnMetadata<
    ManagerWithAggregates,
    ManagerColumns
> = {
    slug: "distribution_platform",
    label: "Distribution Platform",
    type: ColumnTypes.string,
    width: 225,
    maxWidth: 225,
    render: (manager) => {
        const allPlatforms = manager.platform_stats;

        if (!allPlatforms || allPlatforms.length === 0) {
            return <Emdash />;
        }

        if (allPlatforms.length === 1) {
            const platform = allPlatforms[0];

            return (
                <Link
                    className="py-0.5 px-0.5 flex items-center"
                    variant="default"
                    analyticsLabel={
                        AssetManagersUserEvents.TableLinkClickPlatform
                    }
                    analyticsEventProperties={{
                        platform: platform.slug,
                    }}
                    href={getPlatformURL({ platformSlug: platform.slug })}
                >
                    <ImageLoader
                        src={platform.icon_url || RWA_COIN_ICON}
                        alt={`${platform.name} Logo`}
                        fallbackSrc={RWA_COIN_ICON}
                        className="h-5 w-5 min-w-5 rounded-full bg-gray-50 relative inline-block"
                        fallbackClassName="bg-white"
                    />
                    <p className="ml-2 dg-overflow">{platform.name}</p>
                </Link>
            );
        }

        return (
            <div className="py-0.5 px-0.5 isolate flex -space-x-2 overflow-hidden h-full items-center w-full">
                {allPlatforms.map((p) => (
                    <Tooltip
                        key={p.slug}
                        placement="top"
                        tooltipContent={
                            <div className="px-3 py-1.5">
                                <p className="whitespace-nowrap text-sm">
                                    {p.name}
                                </p>
                                <p className="whitespace-nowrap text-xs">
                                    Click to view {p.name} page
                                </p>
                            </div>
                        }
                    >
                        {({ setTriggerRef }) => (
                            <Link
                                setTriggerRef={setTriggerRef}
                                href={getPlatformURL({
                                    platformSlug: p.slug,
                                })}
                                analyticsLabel={
                                    AssetManagersUserEvents.TableLinkClickPlatform
                                }
                                analyticsEventProperties={{
                                    platform: p.slug,
                                }}
                            >
                                <ImageLoader
                                    src={p.icon_url || RWA_COIN_ICON}
                                    alt={`${p.name} Logo`}
                                    fallbackSrc={RWA_COIN_ICON}
                                    className="relative inline-block h-5 w-5 min-w-5 rounded-full bg-gray-50 z-10"
                                    fallbackClassName="bg-white"
                                />
                            </Link>
                        )}
                    </Tooltip>
                ))}
            </div>
        );
    },
};

export const NETWORKS_COLUMN: ColumnMetadata<
    ManagerWithAggregates,
    ManagerColumns
> = {
    slug: "networks",
    label: "Networks",
    type: ColumnTypes.string,
    width: 225,
    maxWidth: 225,
    render: (manager) => {
        const allNetworks = manager.network_stats;

        if (!allNetworks || allNetworks.length === 0) {
            return <Emdash />;
        }

        if (allNetworks.length === 1) {
            const network = allNetworks[0];

            return (
                <Link
                    className="py-0.5 px-0.5 flex items-center"
                    variant="default"
                    analyticsLabel={
                        AssetManagersUserEvents.TableLinkClickNetwork
                    }
                    analyticsEventProperties={{
                        network: network.slug,
                    }}
                    href={getNetworkURL({ networkSlug: network.slug })}
                >
                    <ImageLoader
                        src={network.icon_url || RWA_COIN_ICON}
                        alt={`${network.name} Logo`}
                        fallbackSrc={RWA_COIN_ICON}
                        className="h-5 w-5 min-w-5 rounded-full bg-gray-50 relative inline-block"
                        fallbackClassName="bg-white"
                    />
                    <p className="ml-2 dg-overflow">{network.name}</p>
                </Link>
            );
        }

        return (
            <div className="py-0.5 px-0.5 isolate flex -space-x-2 overflow-hidden h-full items-center w-full">
                {allNetworks.map((n) => (
                    <Tooltip
                        key={n.slug}
                        placement="top"
                        tooltipContent={
                            <div className="px-3 py-1.5">
                                <p className="whitespace-nowrap text-sm">
                                    {n.name}
                                </p>
                                <p className="whitespace-nowrap text-xs">
                                    Click to view {n.name} page
                                </p>
                            </div>
                        }
                    >
                        {({ setTriggerRef }) => (
                            <Link
                                setTriggerRef={setTriggerRef}
                                href={getNetworkURL({
                                    networkSlug: n.slug,
                                })}
                                analyticsLabel={
                                    AssetManagersUserEvents.TableLinkClickNetwork
                                }
                                analyticsEventProperties={{
                                    network: n.slug,
                                }}
                            >
                                <ImageLoader
                                    src={n.icon_url || RWA_COIN_ICON}
                                    alt={`${n.name} Logo`}
                                    fallbackSrc={RWA_COIN_ICON}
                                    className="relative inline-block h-5 w-5 min-w-5 rounded-full bg-gray-50 z-10"
                                    fallbackClassName="bg-white"
                                />
                            </Link>
                        )}
                    </Tooltip>
                ))}
            </div>
        );
    },
};
